import { ReactNode, useLayoutEffect, useRef } from 'react';
import SendbirdProvider from '@sendbird/uikit-react/SendbirdProvider';
import { Box, CircularProgress, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { useSendbirdConnection } from '../../hooks/useSendbirdConnection';
import { ConversationMessagesSkeleton } from '../pages-components/chat-page-components/MessagingSkeleton/ConversationSkeleton';
import { neutralizeHeadlessSendbirdPortals } from '../../utils/sendbird/neutralizeHeadlessSendbirdPortals';
import './sendbirdUIKitProvider.scss';

export type SendbirdUIKitProviderProps = {
  isNewModelChannel?: boolean;
  vendorSendBirdId?: string | null;
  channelUrl?: string;
  children: ReactNode;
  className?: string;
  /** Hidden SDK-only provider (mention sync) — must not affect chat layout. */
  headless?: boolean;
  /** List-row host (e.g. inbox support channel) — size to content, do not flex-grow. */
  compact?: boolean;
  embedded?: boolean;
  uikitOptions?: React.ComponentProps<typeof SendbirdProvider>['uikitOptions'];
};

function getProviderLayoutSx(headless: boolean, compact: boolean): SxProps<Theme> {
  if (headless) {
    return {
      display: 'none',
      height: 0,
      width: 0,
      overflow: 'hidden',
      position: 'absolute',
      pointerEvents: 'none',
    };
  }

  if (compact) {
    return {
      display: 'flex',
      flexDirection: 'column',
      flex: '0 0 auto',
      height: 'auto',
      minHeight: 0,
      minWidth: 0,
    };
  }

  return {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    minWidth: 0,
  };
}

/**
 * NAZ-604: Connects Sendbird UIKit with Baqaal session-token (new model) or vendor owner (legacy).
 */
export const SendbirdUIKitProvider = ({
  isNewModelChannel = false,
  vendorSendBirdId = null,
  channelUrl,
  children,
  className,
  headless = false,
  compact = false,
  embedded = false,
  uikitOptions,
}: SendbirdUIKitProviderProps) => {
  const { applicationId, userId, accessToken, isReady, isLoading, error } = useSendbirdConnection(
    isNewModelChannel,
    vendorSendBirdId,
  );

  const rootClassName = [
    'sendbirdUIKitProviderRoot',
    headless ? 'sendbirdUIKitProviderRoot--headless' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const layoutSx = getProviderLayoutSx(headless, compact);
  const providerKey = `${userId}-${channelUrl ?? 'global'}-${isNewModelChannel ? 'new' : 'legacy'}`;
  const rootRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!headless || !isReady) return undefined;
    const root = rootRef.current;
    if (!root) return undefined;

    neutralizeHeadlessSendbirdPortals(root);

    const observer = new MutationObserver(() => {
      neutralizeHeadlessSendbirdPortals(root);
    });
    observer.observe(root, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [headless, isReady, providerKey]);

  if (headless && (error?.message || !isReady)) {
    return null;
  }

  if (error?.message && !isLoading && !isReady) {
    return (
      <Box className={rootClassName} sx={layoutSx}>
        <Typography color="error">{error.message}</Typography>
      </Box>
    );
  }

  if (!isReady && embedded && isLoading) {
    return <ConversationMessagesSkeleton className={rootClassName} />;
  }

  if (!isReady) {
    return (
      <Box className={rootClassName} sx={layoutSx}>
        {!headless && isLoading ? (
          <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flex: compact ? undefined : 1,
        minHeight: compact ? undefined : 0,
        py: compact ? 1 : undefined
      }}>
            <CircularProgress size={compact ? 20 : 32} />
          </Box>
        ) : null}
      </Box>
    );
  }

  return (
    <Box ref={rootRef} className={rootClassName} sx={layoutSx}>
      <SendbirdProvider
        key={providerKey}
        appId={applicationId}
        userId={userId}
        accessToken={accessToken}
        uikitOptions={uikitOptions}
      >
        {children}
      </SendbirdProvider>
    </Box>
  );
};

export default SendbirdUIKitProvider;
